<template>
  <div id="primaryMenu" :class="{isMobile}">
    <!-- TODO: This belongs in the header component -->
    <!-- We have to v-if instead of v-show here as v-show hides _all_ instances of BrandLogo on the page -->
    <div v-if="!isMobile || (isHeader && !showMenuToggle)" class="menuItem logo">
      <router-link aria-label="Home" :to="{ name: 'Home' }" class="root" :tabindex="-1">
        <img
          src="@/assets/images/Logotype.svg"
          title="Home" />
      </router-link>
    </div>

    <div
      v-else-if="isHeader && isMobile && showMenuToggle"
      class="menuItem navToggle"
      :class="{ appStyle: activeAppId}"
      :style="activeAppId && activeAppPattern ? { background: activeAppPattern.gradient} : undefined">
      <button aria-label="Menu" @click="toggleMobileSideMenu">
        <!-- This is the compact version so that it's visible on the app gradient. If we use the light variant on the
             white background then it's visible, but displays slightly larger than the compact variant, which looks
             a bit odd when changing contexts. -->
        <BrandIcon name="compact/burger-menu-compact" title="Menu"></BrandIcon>
      </button>
    </div>

    <template v-if="isMenuItemVisible">
      <div class="menuItem home" ref="homeItem">
        <router-link aria-label="Home" :to="{ name: 'Home' }" class="logo" title="Home">
          <div class="selInd">
            <BrandIcon v-show="isHomeRoot" name="flat/home-solid" :showFocus="false" />
            <BrandIcon v-show="!isHomeRoot" name="flat/home-light" :showFocus="false" />
          </div>
        </router-link>
      </div>

      <div v-if="isMobile">
        <NonAppMenu @clearAllNotifications="$emit('clearAllNotifications')" :isHeader="isHeader" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { debounce } from 'lodash-es';
import {
  computed,
  ComputedRef,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  Ref,
  ref,
  watch,
} from 'vue';

import BrandIcon from '@/components/icons/BrandIcon.vue';
import { useRoute } from '@/composables/router/VueRouter';
import { AppPattern } from '@/data/Branding';
import BrowserDetection from '@/data/BrowserDetection';
import { patternForApp } from '@/data/helpers/TaskHelper';
import { MiniApp } from '@/data/tasks/MiniApp';
import RouteNames from '@/router/RouteNames';
import { useTasksStore } from '@/stores/Tasks';
import { useTrackEntriesStore } from '@/stores/TrackEntries';
import { useTracksStore } from '@/stores/Tracks';
import { useUIStateStore } from '@/stores/UIState';

const NonAppMenu = defineAsyncComponent(() => import('@/NonAppMenu.vue'));

const tracksStore = useTracksStore();
const trackEntriesStore = useTrackEntriesStore();

const tasksStore = useTasksStore();
const uiStateStore = useUIStateStore();

const route = useRoute();

interface Props {
  // This one is required for mobile, as we show some icons in footer too
  isHeader?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  isHeader: true,
});

interface Emits {
  (e: 'clearAllNotifications'): void,
}
defineEmits<Emits>();

const itemsHolder: Ref<HTMLDivElement | null> = ref(null);
const homeItem: Ref<HTMLDivElement | null> = ref(null);

const routeName: Ref<string> = ref('');

// TODO: on first render grab the home icon width
const itemWidth: Ref<number> = ref(69);
const maxWidth: Ref<number> = ref(0);
const itemWidthAttempt: Ref<number> = ref(0);

const menuWidthResizeObserver: Ref<ResizeObserver | null> = ref(null);

const debouncedHandleResize = debounce(handleResize, 20, {
  maxWait: 50,
  leading: true,
  trailing: true,
});

const activeAppId: ComputedRef<string | undefined> = computed(() => {
  return tracksStore.activeAppId;
});

const showMenuToggle: ComputedRef<boolean> = computed(() => {
  return ![
    '',
    RouteNames.HOME_ROUTE_NAME,
    RouteNames.PROFILE_SETTINGS_ROUTE_NAME,
    RouteNames.TASKS_ROUTE_NAME,
    RouteNames.NOTIFICATIONS_ROUTE_NAME,
    RouteNames.ICONS_ROUTE_NAME,
    RouteNames.COMPONENTS_ROUTE_NAME,
    RouteNames.TASKS_TABLE_ROUTE_NAME,
    RouteNames.APP_TABLE_ROUTE_NAME,
    RouteNames.APP_ROUTE_NAME,
    RouteNames.REPORTING_ROUTE_NAME,
    RouteNames.REPORTING_TENANT_ROUTE_NAME,
  ].includes(routeName.value);
});

const patternByApp: ComputedRef<Record<string, AppPattern>> = computed(() => {
  const patternByApp: Record<string, AppPattern> = {};
  tasksStore.tenantAppsVisibleToUser.forEach((app: MiniApp) => {
    patternByApp[app.id] = patternForApp(app);
  });
  return patternByApp;
});

const activeAppPattern: ComputedRef<AppPattern | undefined> = computed(() => {
  if (!activeAppId.value) {
    return;
  }
  return patternByApp.value[activeAppId.value];
});

const isHomeRoot: ComputedRef<boolean> = computed(() => {
  return route.name === RouteNames.HOME_ROUTE_NAME;
});

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

const isMenuItemVisible: ComputedRef<boolean> = computed(() => {
  return (isMobile.value !== props.isHeader);
});

function toggleMobileSideMenu(): void {
  uiStateStore.toggleMobileSideMenuOpen();
  if (routeName.value !== RouteNames.KNOWLEDGE_MANAGEMENT_ROUTE_NAME) {
    if (trackEntriesStore.currentlyDisplayedFile) {
      trackEntriesStore.setCurrentlyDisplayedFile(null);
    }
  }
}

function handleResize(): void {
  // By requesting an animation frame as well as debouncing, we handle the edges where one debounce ends due to
  // the maxWait and the next starts
  requestAnimationFrame(() => {
    intHandleResize();
  });
}

function intHandleResize(): void {
  maxWidth.value = itemsHolder.value?.clientWidth ?? 0;
}

function updateItemWidth(): void {
  if (++itemWidthAttempt.value >= 20) {
    return;
  }
  nextTick(() => {
    const width: number = homeItem.value?.offsetWidth ?? 0;
    if (!width) {
      setTimeout(updateItemWidth, 100);
      return;
    }
    itemWidth.value = width;
  });
}

watch(() => route.name, (newValue: string) => {
  // Set via a watcher as it wasn't reliably recalculating when using directly in a getter
  routeName.value = newValue;
}, { immediate: true });

onMounted(() => {
  if (!isMobile.value) {
    nextTick(() => {
      menuWidthResizeObserver.value = new ResizeObserver(debouncedHandleResize);
      if (itemsHolder.value) {
        menuWidthResizeObserver.value.observe(itemsHolder.value);
      }
    });
    updateItemWidth();
  }
});

onBeforeUnmount(() => {
  menuWidthResizeObserver.value?.disconnect();
  menuWidthResizeObserver.value = null;
});
</script>

<style scoped lang="scss">
#primaryMenu {
  width: 100%;
  display: flex;

  .items {
    display: flex;
    flex: 1 1;
    overflow: hidden;
  }

  .brandIconContainer {
    width: 2rem;
    height: 2rem;
  }

  *:focus {
    outline: none;
  }

  .mobile & {
    flex-direction: row;
    width: auto;
    justify-content: space-around;
    align-items: center;
    height: 3.5rem;
  }

  .menuItem {
    height: 2.5rem;
    overflow: hidden;
    height: 4.25rem;
    border-right: 1px solid #E3E4E7;

    :focus-visible .selInd {
      width: calc(100% - 4px);
      height: calc(100% - 4px);
      outline: 2px solid $colour-border-emphasis-highlight; // Designs had #3183FF;
      border-width: 2px 0;
    }

    &:not(.logo) {
      width: 4.25rem;
      min-width: 4.25rem;
      .mobile & {
        width: auto;
        min-width: unset;
        height: 100%;
      }
    }
    &.logo {
      padding-right: 0.875rem;
      min-width: 5.875rem;
      padding-top: 3px;
      padding-left: 0.25rem;
      box-sizing: border-box;
      .mobile & {
        max-height: 100%;
        box-sizing: border-box;
      }
      img {
        height: 2.5rem;
      }
    }

    a {
      border: 0;
      border-radius: 0;
      &:not(.router-link-active, .selected) {
        .selInd {
          border-bottom-color: transparent !important;
        }
        .brandIconContainer {
          color: $colour-icon-primary-mid-dark !important;
        }
      }
    }

    .selInd {
      border-width: 4px 0;
      border-style: solid;
      border-color: transparent;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &.home a {
      &.router-link-exact-active {
        color: $color-menu-highlight;
      }
    }
    .brandIconContainer {
      &.selected {
        display: none;
      }
    }

    .selected, .router-link-exact-active, .router-link-active:not(.root):not(.logo) {
      &:not(.root) {
        .selInd {
          border-bottom-color: $color-menu-highlight;
        }
      }
      .brandIconContainer {
        &.deselected {
          display: none;
        }
        &.selected {
          display: block;
        }
      }
      :deep(.focusIndication) {
        opacity: 1;
        color: $colour-link-emphasis;
      }
    }
    .router-link-exact-active.root {
      .mobile & {
        .logo {
          width: 3rem;
          height: 3rem;
        }
        :deep(.focusIndication) {
          opacity: 0;
        }
      }
    }

    a, :deep(button) {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      box-sizing: border-box;
      cursor: pointer;
      color: $colour-icon-primary-mid-dark;
    }

    .mobile & {
      width: 100%;

      &:last-child {
        border-right: none;
      }
      &.navToggle {
        width: 3.5rem;
        &.appStyle {
          .brandIconContainer {
            color: $colour-icon-contrast;
          }
        }
      }
    }

    &.apps {
      .overflowOpener {
        display: flex;
        gap: 0.5rem;
      }
      &.selected {
        width: 6.875rem;
        min-width: 6.875rem;
        flex: 0 0 6.875rem;
      }
      .selected {
        .brandIconContainer {
          color: $color-menu-highlight;
        }
      }
      :deep(.menuSection) {
        .selected {
          .brandIconContainer {
            color: var(--app-colour, $color-menu-highlight);
          }
        }
      }
    }
  }

  .prefixIcon {
    font-size: 1.5rem;
    line-height: 1.5rem;

    .mobile & {
      font-size: 2rem;
      line-height: 2rem;
    }
  }

  .itemText {
    font-size: $size-font-nav-list;
    font-weight: $weight-bold;
    margin-top: 0.1rem;
  }
}
.iconMask {
  mask-repeat: 'no-repeat';
  width: 2rem;
  height: 2rem;
}
</style>
